import type { ColumnDef } from '@tanstack/react-table'
import type { InternalUser } from '@/hooks/useInternalUsers'
import { Badge } from '@/components/ui/badge'
import { CheckCircle2, Flag } from 'lucide-react'
import CopyButton from '@/components/common/CopyButton'

const statusStyles: Record<InternalUser['status'], string> = {
    Active: 'bg-green-100 text-green-700 hover:bg-green-100',
    Inactive: 'bg-gray-100 text-gray-700 hover:bg-gray-100',
    Suspended: 'bg-red-100 text-red-700 hover:bg-red-100',
}

export const adminUserColumns: ColumnDef<InternalUser>[] = [
    {
        accessorKey: 'id',
        header: 'User ID',
        cell: ({ row }) => (
            <span className="font-mono text-xs text-[#0F172A]">
                #{row.original.id}
            </span>
        ),
    },
    {
        accessorKey: 'fullName',
        header: 'Full name',
        cell: ({ row }) => (
            <span className="text-sm text-[#0F172A]">
                {row.original.fullName}
            </span>
        ),
    },
    {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => (
            <div className="group inline-flex items-center">
                <span className="text-sm text-muted-foreground">
                    {row.original.email}
                </span>
                <CopyButton className="ml-1" text={row.original.email} />
            </div>
        ),
    },
    {
        accessorKey: 'phoneNumber',
        header: 'Phone no',
        cell: ({ row }) => (
            <div className="group inline-flex items-center">
                <span className="text-sm text-muted-foreground">
                    {row.original.phoneNumber}
                </span>
                <CopyButton className="ml-1" text={row.original.phoneNumber} />
            </div>
        ),
    },
    {
        accessorKey: 'gender',
        header: 'Gender',
        cell: ({ row }) => (
            <span className="text-sm text-[#0F172A]">
                {row.original.gender === 'MALE'
                    ? 'M'
                    : row.original.gender === 'FEMALE'
                      ? 'F'
                      : row.original.gender}
            </span>
        ),
    },
    {
        accessorKey: 'role',
        header: 'Role',
        cell: ({ row }) => (
            <span className="text-sm text-[#0F172A]">{row.original.role}</span>
        ),
    },
    {
        accessorKey: 'dateAdded',
        header: 'Date added',
        cell: ({ row }) => (
            <span className="text-sm text-muted-foreground">
                {row.original.dateAdded}
            </span>
        ),
    },
    {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
            const status = row.original.status
            const style = statusStyles[status] ?? statusStyles.Active
            return (
                <Badge variant="secondary" className={style}>
                    {status === 'Active' ? (
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                    ) : status === 'Suspended' ? (
                        <Flag className="mr-1 h-3 w-3" />
                    ) : null}
                    {status}
                </Badge>
            )
        },
    },
]
